import axios from "axios";
import { axiosPublic } from "./axiosPublic";

const axiosApiInstance = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Helper function to check if user is active on admin pages
const isUserActiveOnAdminPage = () => {
  const currentPath = window.location.pathname;
  const isAdminPage = currentPath.startsWith("/admin");

  if (!isAdminPage) return false;

  const lastActivity = localStorage.getItem("lastUserActivity");
  if (!lastActivity) return false;

  const now = Date.now();
  const timeDiff = now - parseInt(lastActivity);
  const minutesDiff = timeDiff / (1000 * 60);

  // Consider user active if last activity was within 5 minutes
  return minutesDiff < 5;
};

// Helper function to extend session for active users
const extendSessionForActiveUser = async () => {
  try {
    const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const lastActivity = localStorage.getItem("lastUserActivity");

    if (!userDetails.id || !lastActivity) {
      return null;
    }

    const response = await axiosPublic.post("extend-session", {
      user_id: userDetails.id,
      last_activity: lastActivity,
    });

    if (response.data && response.data.token) {
      localStorage.setItem("userData", JSON.stringify(response.data.token));
      localStorage.setItem(
        "userRefreshToken",
        JSON.stringify(response.data.refresh_token),
      );
      console.log("Session extended for active user");
      return response.data.token;
    }

    return null;
  } catch (error) {
    console.error("Error extending session:", error);
    return null;
  }
};

// Helper function to clear user data and redirect to home
const clearUserDataAndRedirect = () => {
  // Check if user is active on admin page before redirecting
  if (isUserActiveOnAdminPage()) {
    console.log("User is active on admin page, not redirecting");
    return false; // Don't redirect
  }

  localStorage.removeItem("userData");
  localStorage.removeItem("userRefreshToken");
  localStorage.removeItem("userDetails");
  localStorage.removeItem("lastUserActivity");
  // Redirect to home page
  window.location.href = "/";
  return true; // Redirected
};

axiosApiInstance.interceptors.request.use(
  async (config) => {
    const userToken = JSON.parse(localStorage.getItem("userData") || "{}");

    config.headers = {
      Authorization: `Bearer ${userToken}`,
      Accept: "application/json",
    };
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosApiInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error) {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh the access token
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Update headers with new token
          console.log("move to new token");
          axiosApiInstance.defaults.headers.common["Authorization"] =
            `Bearer ${newToken}`;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // Retry the original request with new token
          return axiosApiInstance(originalRequest);
        } else {
          // If no token returned, try to extend session for active users
          if (isUserActiveOnAdminPage()) {
            const extendedToken = await extendSessionForActiveUser();
            if (extendedToken) {
              // Update headers with extended token
              axiosApiInstance.defaults.headers.common["Authorization"] =
                `Bearer ${extendedToken}`;
              originalRequest.headers.Authorization = `Bearer ${extendedToken}`;
              return axiosApiInstance(originalRequest);
            }
          }

          // If extension failed or user not active, redirect
          const redirected = clearUserDataAndRedirect();
          if (!redirected) {
            console.log("Continuing with expired tokens due to user activity");
            return Promise.resolve({
              data: { message: "Token expired but user active" },
            });
          }
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // If refresh token fails, check user activity before redirecting
        console.error("Token refresh failed:", refreshError);
        const redirected = clearUserDataAndRedirect();
        if (!redirected) {
          // User is active on admin page, allow continued operation
          console.log("Token refresh failed but user is active on admin page");
          return Promise.resolve({
            data: { message: "Token refresh failed but user active" },
          });
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

const refreshAccessToken = async () => {
  return new Promise(async (resolve, reject) => {
    try {
      const refreshToken = JSON.parse(
        localStorage.getItem("userRefreshToken") || "{}",
      );

      // Check if refresh token exists
      if (!refreshToken || Object.keys(refreshToken).length === 0) {
        console.error("No refresh token found");
        const redirected = clearUserDataAndRedirect();
        if (!redirected) {
          // User is active on admin page, resolve with null to indicate no token but continue
          resolve(null);
          return;
        }
        reject(new Error("No refresh token available"));
        return;
      }

      // Call refresh token API
      const response = await axiosPublic.post("refresh/token", {
        refresh_token: refreshToken,
      });

      // Store new tokens
      if (response.data && response.data.token) {
        localStorage.setItem("userData", JSON.stringify(response.data.token));
        localStorage.setItem(
          "userRefreshToken",
          JSON.stringify(response.data.refresh_token),
        );
        resolve(response.data.token);
      } else {
        console.error("Invalid response from refresh token API");
        const redirected = clearUserDataAndRedirect();
        if (!redirected) {
          // User is active on admin page, resolve with null
          resolve(null);
          return;
        }
        reject(new Error("Invalid refresh token response"));
      }
    } catch (error) {
      // If refresh token API fails, check user activity before redirecting
      console.error("Error refreshing access token:", error);
      const redirected = clearUserDataAndRedirect();
      if (!redirected) {
        // User is active on admin page, resolve with null
        resolve(null);
        return;
      }
      reject(error);
    }
  });
};

export default axiosApiInstance;
